import { useTranslation } from 'react-i18next';
import { GridAlignment, GridColDef } from '@mui/x-data-grid';
import { useState, ChangeEvent, useEffect, useContext } from 'react';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../constants/numbers';
import BaranDataGrid from '../common/BaranDataGrid';
import { filterList } from '../../utils/array';
import IResponse, { IPagination } from '../../interfaces/response';
import axios from 'axios';
import { Box, IconButton } from '@mui/material';
import { exportToExcel } from '../../utils/file';
import { useDebounce } from 'use-debounce';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditPriceModal from './EditPriceModal.tsx';
import { isNull } from 'lodash';
import { useParams } from 'react-router-dom';
import { CampaignContext } from '../../contexts/CampaignContext.tsx';

interface ICampaignViewPricing {
	id?: number;
	view?: number;
	price?: number;
	media?: string;
	type: string;
}

export default function CampaignViewPricing() {
	const { fetchedMedias } = useContext(CampaignContext);
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [fetchedData, setFetchedData] = useState<ICampaignViewPricing[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(false);
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);
	const [open, setOpen] = useState(false);
	const [id, setId] = useState<number | null>(null);
	const [isCreate, setIsCreate] = useState(false);
	const { type } = useParams();
	const handleClose = () => {
		setOpen(false);
		setId(null);
		getServer();
		setIsCreate(false);
	};

	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const handleExportExcel = () => {
		exportToExcel({ data: filteredRows, fileName: 'payment_list', t });
	};

	const handleNew = () => {
		setIsCreate(true);
		setOpen(true);
	};

	const properties: (keyof ICampaignViewPricing)[] = ['view', 'price', 'media'];

	const filteredRows = filterList(fetchedData, properties, searchTerm);
	const handleClickOpen = (id: number) => {
		setOpen(true);
		setId(id);
	};

	const handleClickDelete = (id: number) => {
		if (!isNull(id)) {
			axios({
				url: `/admin/campaign-view-price/${id}`,
				method: 'DELETE',
				loading: true,
			}).then(() => {
				getServer();
			});
		}
	};

	const columns: GridColDef<ICampaignViewPricing>[] = [
		{
			field: 'id',
			headerName: t('row'),
			width: 90,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				const rowIndex = filteredRows.indexOf(params.row);
				return (page - 1) * pageSize + (rowIndex + 1);
			},
		},
		{
			field: 'view',
			headerName: t('view_count'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'price',
			headerName: t('view_price'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'media',
			headerName: t('media'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: (params: any) => {
				let filterMedia = fetchedMedias?.find(item => item.system_name === params.row.media);
				return <>{filterMedia?.title}</>;
			},
		},

		{
			field: 'action',
			headerName: t('action'),
			flex: 1,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: (params: any) => {
				return (
					<>
						<IconButton onClick={() => handleClickOpen(params?.row?.id)}>
							<EditIcon className="text-text-secondary" />
						</IconButton>
						<IconButton onClick={() => handleClickDelete(params?.row?.id)}>
							<DeleteOutlineIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	const getServer = () => {
		setLoading(true);
		let search = JSON.stringify([
			{
				field: 'type',
				value: type,
			},
		]);
		axios<IResponse<ICampaignViewPricing[]>>({
			url: '/admin/campaign-view-price',
			method: 'GET',
			params: {
				page: page,
				limit: pageSize,
				search: search,
				type: type,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(
						res.data.data.map(payment => ({
							...payment,
						})),
					);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		getServer();
	}, [page, pageSize, type]);

	useEffect(() => {
		if (page === 1) {
			getServer();
		} else {
			setPage(1);
		}
	}, [search]);

	return (
		<Box className="w-full">
			<BaranDataGrid<ICampaignViewPricing> pagination={pagination} loading={loading} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} onClick={fetchedData.length ? handleExportExcel : undefined} actionButtonLable={t('export_excel')} onClick2={handleNew} actionButtonLable2={t('new')} searchableFields={properties} />
			{open ? <EditPriceModal open={open} title={t('edit_price_and_view_required_add')} onClose={handleClose} type={type} id={id} isCreate={isCreate} /> : ''}
		</Box>
	);
}
